import React, { FunctionComponent, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  MDBBtn,
  MDBModal,
  MDBModalBody,
  MDBModalFooter,
  MDBModalHeader,
} from "mdbreact";

interface AlertModalProps {
  isOpen?: boolean;
  title?: string;
  body?: string;
  positive?: string;
  negative?: string;
  onPositive?: (e?: any) => void;
  onNegative?: (e?: any) => void;
  onDismiss?: (e?: any) => void;
}

const AlertModal: FunctionComponent<AlertModalProps> = (props) => {
  const [open, setOpen] = useState(false);

  /**
   * Follows `isOpen` in BOTH directions.
   *
   * This used to be a one-way `if (props.isOpen) setOpen()` over a toggle,
   * which meant the modal could only ever be closed by its own buttons — a
   * parent that set `isOpen` back to false was ignored. Every earlier caller
   * (Header, SidebarBottomMenu) only opens the modal and lets it close
   * itself, so nothing exercised it. SessionIdleWarningModal is the first
   * caller that closes it from outside: when a member becomes active again,
   * possibly in another tab, the idle clock lowers the warning with no click
   * involved. Under the old behaviour the dialog stayed on screen with a
   * frozen countdown, and a member alarmed by a stale "signing out" prompt
   * could click Sign Out — producing the exact outcome PPI-1848 exists to
   * prevent.
   */
  useEffect(() => {
    setOpen(Boolean(props.isOpen));
  }, [props.isOpen]);

  function toggle() {
    // Only reachable while the dialog is on screen — MDBModal renders no
    // close control or backdrop otherwise. The old `if (open)` guard existed
    // because the setter blindly flipped; the close is explicit now.
    setOpen(false);
    props.onDismiss?.();
  }

  function onNegative() {
    setOpen(false);
    props.onNegative?.();
  }

  function onPositive() {
    setOpen(false);
    props.onPositive?.();
  }

  // Portaled straight to body: MDBModal renders in place in the React tree and
  // relies on position: fixed to visually escape its ancestors, which breaks
  // (clicks silently swallowed) the moment any ancestor gets an overflow/clip
  // rule -- e.g. the sidebar's sticky scroll area (PPI-1391). Portaling removes
  // the dependency on every ancestor between here and <body> staying clip-free.
  return ReactDOM.createPortal(
    <MDBModal
      isOpen={open}
      toggle={toggle}
      inline={false}
      noClickableBodyWithoutBackdrop={false}
      overflowScroll={true}
      centered
    >
      {props.title && (
        <MDBModalHeader toggle={toggle} className={"border-0"}>
          {props.title}
        </MDBModalHeader>
      )}
      {props.body && (
        <MDBModalBody className={"py-0"}>{props.body}</MDBModalBody>
      )}
      {(props.positive || props.negative) && (
        <MDBModalFooter className={"border-0"}>
          {props.negative && (
            <MDBBtn
              flat
              onClick={onNegative}
              color={"light"}
              className={"btn-flat grey-text px-3 py-2"}
            >
              {props.negative}
            </MDBBtn>
          )}
          {props.positive && (
            <MDBBtn
              onClick={onPositive}
              className={"btn-flat text-primary px-3 py-2"}
            >
              {props.positive}
            </MDBBtn>
          )}
        </MDBModalFooter>
      )}
    </MDBModal>,
    document.body
  );
};

AlertModal.defaultProps = {
  isOpen: false,
};

export default AlertModal;
